import { Button } from 'common/Components/Button';
import Captcha from 'common/Components/Captcha';
import Input from 'common/Components/form/Input';
import { useRouter } from 'common/hooks/useRouter';
import { toEnglishNumber } from 'common/method/helper';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { setAccountActivationStep } from 'redux/common/AccountActivationSlice';
import { useActivateAccount } from 'services/AccountActivation';
import { useGetCaptch } from 'services/login';

type FormType = {
  nationalCode: string;
  mobile: string;
  captchaValue: string;
};

const ActivationForm = () => {
  //
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { navigate } = useRouter();

  const { data: captcha, refetch: refetchCaptcha } = useGetCaptch();

  const handleRefetchCaptch = (): void => {
    refetchCaptcha();
    resetField('captchaValue');
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    setValue,
    getValues,
  } = useForm<FormType>({
    defaultValues: {
      nationalCode: '',
      mobile: '',
      captchaValue: '',
    },
    mode: 'onChange',
  });

  const { mutateAsync, isLoading } = useActivateAccount({
    onSuccess: ({ result, succeeded }) => {
      if (!result || !succeeded) return;

      if (result.registrationStatus === 'InvalidCaptcha') {
        handleRefetchCaptch();
        toast.error(t('Errors.InvalidCaptcha'));
        return;
      }

      if (result.rivasUser) {
        return dispatch(setAccountActivationStep('AlreadyActived'));
      }

      if (!result.farabiUser) {
        return dispatch(setAccountActivationStep('ShouldRegister'));
      }

      if (result.farabiUser && !result.rivasUser) {
        toast.info('در حال انتقال به صفحه فعالسازی ریواس');
        setTimeout(() => {
          window.location.replace('https://help.irfarabi.com/rivas/');
        }, 1500);
        // dispatch(setAccountActivationStep('ActivedSuccessfully'));
      }
    },
    onError: () => {
      toast.error('فعالسازی حساب کاربری با خطا مواجه شد');
    },
  });

  const onSubmitForm: SubmitHandler<FormType> = ({
    mobile,
    nationalCode,
    captchaValue,
  }) => {
    mutateAsync({
      mobile,
      nationalCode,
      captchaValue: toEnglishNumber(captchaValue),
      captchaKey: captcha?.key || '',
    });
  };

  const onRefuse = () => {
    dispatch(setAccountActivationStep('Agreement'));
    navigate.pushLogin();
  };

  return (
    <div>
      <h1 className="text-5xl font-bold text-center text-L-primary-50 mt-4">
        {t(`BrokerCode_${window.api?.BROKER_CODE}.Activation.account_request`)}
      </h1>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <Input
          placeholder={t(`FormSide.Input.NationalCode.Placeholder`)}
          classparent="mt-4"
          classInput="ltrInsert"
          type={'number'}
          label={t(`FormSide.Input.NationalCode.Label`)}
          data-cy="nationalCode"
          textError={errors.nationalCode?.message}
          {...register('nationalCode', {
            required: {
              value: true,
              message: t('Errors.Input.Required', {
                n: t('FormSide.Input.NationalCode.Label'),
                interpolation: { escapeValue: false },
              }),
            },
            pattern: {
              value: /^([0-9]){10}$/,
              message: t('Errors.NationalCode_Invalid'),
            },
          })}
        />
        <Input
          placeholder={t(`FormSide.Input.PhoneNumber.Placeholder`)}
          classparent="mt-6"
          classInput="ltrInsert"
          type={'number'}
          label={t(`FormSide.Input.PhoneNumber.Label`)}
          data-cy="mobile"
          textError={errors.mobile?.message}
          {...register('mobile', {
            required: {
              value: true,
              message: t('Errors.Input.Required', {
                n: t('FormSide.Input.PhoneNumber.Label'),
                interpolation: { escapeValue: false },
              }),
            },
            pattern: {
              value: /^09[0-9]{9}$/,
              message: t('Errors.PhoneNumber_Invalid'),
            },
          })}
        />
        <div className="mt-10">
          <Captcha
            register={register}
            errors={errors}
            captcha={captcha}
            onRefresh={handleRefetchCaptch}
            setValue={setValue}
            getValues={getValues}
          />
        </div>

        <div className={'mt-8'}>
          <Button
            disabled={isLoading}
            data-cy="submit"
            className="w-full bg-L-secondary-50 dark:bg-D-secobg-L-secondary-50 rounded text-L-basic dark:text-D-basic p-2 text-3xl mb-6"
          >
            {t(`FormSide.Send.Request`)}
          </Button>
          <p
            onClick={onRefuse}
            className="text-center cursor-pointer text-L-primary-50 text-xl md:text-b2xl lg:text-2xl mb-2"
          >
            {t('FormSide.BackText')}
          </p>
        </div>
      </form>
    </div>
  );
};

export default ActivationForm;
